<style scoped>
  a {
    color: #676a6c;
    text-decoration: none;
  }

  .sla-client-name {
    font-size: 15px;
    font-weight: 500;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    margin-bottom: 0;
  }

  .sla-hours-secondary {
    color: #999;
  }

  .report-sent-label {
    display: block;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #1ab394;
    margin-top: 2px;
  }

  .report-sent-label i {
    margin-right: 2px;
  }

  .sla-progress-col {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .sla-pct {
    flex: 0 0 36px;
    font-size: 13px;
    font-weight: 600;
    color: #555;
    text-align: right;
    white-space: nowrap;
  }

  .sla-progress-bar-wrap {
    flex: 1;
    position: relative;
  }

  .no-schedule-badge {
    font-size: 11px;
    color: #dc3545;
  }
</style>

<template>
  <fragment>
    <router-link
      v-if="scheduleForMonthScope"
      tag="a"
      :to="{ name: 'slas.show', params: { 'sla_id': sla.id }, query: { scheduleId: scheduleForMonthScope ? scheduleForMonthScope.id : -1 }}"
    >
      <div class="row align-items-center">
        <div class="col-auto">
          <span :class="'status ' + classForPercentage(usagePercentage, 'status')"></span>
        </div>

        <div class="col-4">
          <p class="sla-client-name" v-if="sla.client">{{ sla.client.company }}</p>
          <p class="sla-client-name" v-else>{{ sla.notes }}</p>
        </div>

        <div class="col-2 d-none d-md-block">
          <span>{{ scheduleForMonthScope.monthly_hours }}<span class="sla-hours-secondary"> hrs</span></span>
          <span v-if="scheduleForMonthScope.report_sent" class="report-sent-label">
            <i class="fa fa-paper-plane"></i>Report sent
          </span>
        </div>

        <div class="col-2 d-none d-md-block">
          <span>{{ hoursUsed }}</span>
          <span class="sla-hours-secondary"> / {{ totalHours }} hrs</span>
        </div>

        <div class="col-3 sla-progress-col">
          <span class="sla-pct">{{ usagePercentage }}%</span>
          <div class="sla-progress-bar-wrap">
            <div :class="'progress ' + classForPercentage(usagePercentage, 'progress')">
              <div
                class="progress-bar"
                role="progressbar"
                :aria-valuenow="usagePercentage"
                aria-valuemin="0"
                aria-valuemax="100"
                :style="{ width: usagePercentage > 100 ? '100%' : usagePercentage + '%' }"
              ></div>
              <span class="marker" :style="'left: ' + percentageThroughTheMonth + '%;'"></span>
            </div>
          </div>
        </div>
      </div>
    </router-link>

    <div v-else class="row align-items-center" style="padding: 1.4rem 1.5rem; margin: 0;">
      <div class="col-auto">
        <span class="status"></span>
      </div>

      <div class="col-4">
        <p class="sla-client-name" v-if="sla.client">
          {{ sla.client.company }}
          <span class="sla-type-label">{{ sla.type ? sla.type.code : '' }}</span>
        </p>
        <p class="sla-client-name" v-else>{{ sla.notes }}</p>
      </div>

      <div class="col-2 d-none d-md-block">
        <span>{{ sla.monthly_hours }}<span class="sla-hours-secondary"> hrs</span></span>
      </div>

      <div class="col-2 d-none d-md-block">
        <span class="no-schedule-badge"><i class="fa fa-exclamation-circle me-1"></i>No schedule</span>
      </div>

      <div class="col-3 sla-progress-col">
        <span class="sla-pct sla-hours-secondary">—</span>
        <div class="sla-progress-bar-wrap">
          <div class="progress">
            <div class="progress-bar" style="width: 0%"></div>
          </div>
        </div>
      </div>
    </div>
  </fragment>
</template>

<script>
  export default {
    props: [
      'sla',
      'pending',
      'monthScope'
    ],
    data: function () {
      return {}
    },
    computed: {
      momentMonthScope: {
        get: function () {
          return moment(this.monthScope)
        },
        set: function (newValue) {
          this.monthScope = newValue
        }
      },

      percentageThroughTheMonth() {
        if (this.momentMonthScope.isSame(moment().startOf('month'))) {
          return 100
        }

        var currentDay = moment().date()
        var percentage = (currentDay / moment().daysInMonth()) * 100
        var percentageRounded = Math.round(percentage)

        return percentageRounded > 100 ? 100 : percentageRounded
      },

      usagePercentage() {
        return ((this.hoursUsed / this.totalHours) * 100).toFixed(0)
      },

      hoursUsed() {
        if (!this.scheduleForMonthScope) { return 0 }
        var used = parseFloat(this.scheduleForMonthScope.monthly_hours) - parseFloat(this.scheduleForMonthScope.remaining)
        return used.toFixed(2)
      },

      totalHours() {
        if (!this.scheduleForMonthScope) { return this.sla.monthly_hours }
        return (parseFloat(this.scheduleForMonthScope.monthly_hours) + Math.max(parseFloat(this.scheduleForMonthScope.rollover), 0)).toFixed(2)
      },

      scheduleForMonthScope: function () {
        if (!this.sla.schedule) return false

        var diff = this.momentMonthScope.date(1).diff(moment(this.sla.start_date).date(1), 'months')
        var month = diff + 1

        var scheduleItem = this.sla.schedule.find(function (schedule) {
          return schedule.month == month
        })

        return scheduleItem
      }
    },

    methods: {
      classForPercentage: function (percentage, prepend) {
        if (this.pending) return prepend ? prepend + '-info' : 'info'

        if (percentage < 50) {
          return prepend ? prepend + '-success' : 'success'
        } else if (percentage < 70) {
          return prepend ? prepend + '-warning' : 'warning'
        } else {
          return prepend ? prepend + '-danger' : 'danger'
        }
      },
    }
  }
</script>
